/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { getAxiosForClient } from '@/lib/axios';
import {
  type QueryClient,
  type UseQueryOptions,
  useQuery,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import type { FormattedContentStackCategoryPages } from '@/features/contentstack/interface';
import { getCategoryContent } from '@/features/contentstack/lib/category/getCategoryContent'; // eslint-disable-line no-restricted-imports
import { type PageContextType } from '@/next-types';
import { usePageType } from '../api/getPageType';
import { pageTypes } from '@/constants/page';
import { useSegments } from '@/features/header/api/getSegments';
import type { YMMSelectorOptions } from '@/features/contentstack/interface/models';

const URL = `/api/contentstack/category`;

const getCMSCategoryData = async ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof getCategoryDataKey>>) => {
  const [, , { segments, taxonomyPath, pageType, make, model, year }] = queryKey;
  const { data } = await getAxiosForClient().get<FormattedContentStackCategoryPages>(URL, {
    params: { taxonomyPath, pageType, segments: segments.join(','), make, model, year },
  });

  return selector(data);
};

const selector = (data: FormattedContentStackCategoryPages): FormattedContentStackCategoryPages =>
  data;

const getCategoryDataKey = ({
  taxonomyPath,
  pageType,
  segments,
  make,
  model,
  year,
}: {
  taxonomyPath: string;
  pageType: string;
  segments: string[];
} & YMMSelectorOptions) =>
  ['cms', 'categorypage', { pageType, segments, taxonomyPath, make, model, year }] as const;

export const useCMSCategoryData = () => {
  const { data: pageTypeData } = usePageType();

  const isCategoryPage = pageTypeData?.pageType === pageTypes.CategoryGrouping;

  const enabled = isCategoryPage;

  const taxonomyPath = pageTypeData?.taxonomyPath ?? '';
  const pageType = pageTypeData?.subPageType ?? '';

  const data = useGetCMSCategoryData({
    enabled,
    taxonomyPath,
    pageType,
    make: pageTypeData?.make ?? null,
    model: pageTypeData?.model ?? null,
    year: pageTypeData?.year ?? null,
  });

  return {
    enabled,
    ...data,
  };
};

const useGetCMSCategoryData = (
  options: Omit<UseQueryOptions, 'queryKey' | 'queryFn'> & {
    taxonomyPath: string;
    pageType: string;
  } & YMMSelectorOptions,
) => {
  const { data: segments, isSuccess } = useSegments();
  return useQuery({
    queryKey: getCategoryDataKey({
      taxonomyPath: options.taxonomyPath,
      pageType: options.pageType,
      segments: segments?.sort() ?? [],
      make: options.make,
      model: options.model,
      year: options.year,
    }),
    queryFn: getCMSCategoryData,
    enabled: options.enabled && isSuccess,
  });
};

export const prefetchCMSCategoryData = (
  queryClient: QueryClient,
  taxonomyPath: string,
  pageType: string,
  context: PageContextType,
  userSegments: string[],
  ymmSelectorOptions: YMMSelectorOptions,
) => {
  return queryClient.prefetchQuery({
    queryKey: getCategoryDataKey({
      taxonomyPath,
      pageType,
      segments: userSegments,
      ...ymmSelectorOptions,
    }),
    queryFn: () =>
      getCategoryContent(
        context.req,
        { ...context.query, taxonomyPath, pageType },
        userSegments,
        ymmSelectorOptions,
      ),
  });
};
